import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Clock, Loader2, Phone } from "lucide-react";
import { MonthCalendar, toKey } from "../calendar/MonthCalendar";
import { bookingApi } from "../../booking/publicApi";
import { BUSINESS, WHATSAPP_LINK, fmtDatePt } from "../../data/site";

export const Availability = () => {
  const [viewDate, setViewDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedKey, setSelectedKey] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const res = await bookingApi.availability(key);
      setData(res.data);
    } catch {
      setData({ closed: false, slots: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const selectDay = (key) => { setSelectedKey(key); load(key); };

  return (
    <section id="disponibilidade" data-testid="availability-section" className="py-24 md:py-32 bg-cream">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-14">
          <span className="font-script text-magenta text-3xl md:text-4xl">agenda</span>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[0.95] mt-2">
            Veja a nossa disponibilidade.
          </h2>
          <p className="font-body text-[#525252] mt-4">
            Escolha um dia para ver os horários livres. Para marcar, é só contactar-nos por telefone ou WhatsApp.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-white border border-[#0A0A0A] rounded-xl p-6"
          >
            <MonthCalendar
              viewDate={viewDate}
              onPrev={() => { setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1)); }}
              onNext={() => { setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1)); }}
              selectedKey={selectedKey}
              onSelectDay={selectDay}
              countByDate={{}}
            />
          </motion.div>

          {/* Slots (read-only) */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-[#0A0A0A] text-white rounded-xl p-6 flex flex-col"
          >
            {!selectedKey ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-[#a3a3a3] py-16">
                <Clock size={38} className="mb-3 text-[#333]" />
                Selecione um dia para ver os horários.
              </div>
            ) : (
              <>
                <h3 className="font-display font-bold text-xl capitalize mb-4">{fmtDatePt(selectedKey)}</h3>
                {loading ? (
                  <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#E20074]" /></div>
                ) : data?.closed ? (
                  <p className="text-[#E20074] py-8 text-center" data-testid="availability-closed">Encerrado neste dia.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6" data-testid="availability-slots">
                    {data?.slots.map((s) => (
                      <div
                        key={s.time}
                        data-testid={`avail-slot-${s.time}`}
                        className={`py-2.5 rounded-lg text-center text-sm border ${
                          s.available
                            ? "border-[#2a2a2a] text-white"
                            : "border-transparent bg-[#1a1a1a] text-[#5a5a5a] line-through"
                        }`}
                      >
                        {s.time}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-auto pt-4 border-t border-[#2a2a2a]">
                  <p className="text-sm text-[#a3a3a3] mb-3">Horários a cinzento já estão ocupados. Para marcar:</p>
                  <div className="flex flex-wrap gap-3">
                    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="availability-whatsapp"
                      className="inline-flex items-center gap-2 bg-[#E20074] hover:bg-[#c00063] transition-colors text-white font-medium px-5 py-2.5 rounded-full text-sm">
                      Marcar no WhatsApp
                    </a>
                    <a href={`tel:+${BUSINESS.phoneRaw}`} data-testid="availability-phone"
                      className="inline-flex items-center gap-2 border border-white/30 hover:border-white transition-colors text-white font-medium px-5 py-2.5 rounded-full text-sm">
                      <Phone size={15} /> {BUSINESS.phoneDisplay}
                    </a>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
